import React, { useState } from 'react';
import { User, UserUpdateRequest } from '../types/auth';
import Modal from './ui/Modal';
import Button from './ui/Button';
import FormField, { Input, Checkbox } from './ui/FormField';

interface UserEditModalProps {
  user: User;
  onClose: () => void;
  onSave: (userData: UserUpdateRequest) => void;
  loading: boolean;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ user, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState<UserUpdateRequest>({
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    specialty: user.specialty || '',
    license_number: user.license_number || '',
    phone: user.phone || '',
    date_of_birth: user.date_of_birth || '',
    is_active: user.is_active,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';

    // Role-specific validation (specialty, license, phone are all optional)

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up form data based on role
    const cleanedData = { ...formData };
    if (user.role !== 'member') {
      delete cleanedData.specialty;
      delete cleanedData.license_number;
    }
    if (user.role !== 'patient') {
      delete cleanedData.phone;
      delete cleanedData.date_of_birth;
    }

    setErrors({});
    onSave(cleanedData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit User"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            htmlFor="first_name"
            label="First Name"
            required
            error={errors.first_name}
          >
            <Input
              id="first_name"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.first_name}
            />
          </FormField>

          <FormField
            htmlFor="last_name"
            label="Last Name"
            required
            error={errors.last_name}
          >
            <Input
              id="last_name"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.last_name}
            />
          </FormField>
        </div>

        <FormField
          htmlFor="email"
          label="Email"
          required
          error={errors.email}
        >
          <Input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            error={!!errors.email}
          />
        </FormField>

        <FormField htmlFor="role" label="Role">
          <div className="px-3 py-2 bg-clinic-cream/50 border border-clinic-primary-lighter rounded-lg text-clinic-muted">
            <span className="capitalize">{user.role}</span>
            <span className="text-clinic-muted ml-1">(Cannot be changed)</span>
          </div>
        </FormField>

        {/* Role-specific fields */}
        {user.role === 'member' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              htmlFor="specialty"
              label="Professional Title"
              required
              error={errors.specialty}
            >
              <Input
                id="specialty"
                type="text"
                name="specialty"
                value={formData.specialty}
                onChange={handleChange}
                placeholder="e.g., Cardiology, Dermatology"
                disabled={loading}
                error={!!errors.specialty}
              />
            </FormField>

            <FormField htmlFor="license_number" label="License Number">
              <Input
                id="license_number"
                type="text"
                name="license_number"
                value={formData.license_number}
                onChange={handleChange}
                disabled={loading}
              />
            </FormField>
          </div>
        )}

        {user.role === 'patient' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              htmlFor="phone"
              label="Phone Number"
              required
              error={errors.phone}
            >
              <Input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                error={!!errors.phone}
              />
            </FormField>

            <FormField htmlFor="date_of_birth" label="Date of Birth">
              <Input
                id="date_of_birth"
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                disabled={loading}
              />
            </FormField>
          </div>
        )}

        <Checkbox
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={loading}
          label="Active Account"
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-clinic-primary-lighter">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserEditModal;
